// #27 Slots (proposed slots) with the row 100 slot extension. A radiogroup of slot cards (DS DC-137: the radio cards beside
// the free/busy grid): one tab stop, arrows rove with wrap and select (selection follows focus), Home/End jump, Space
// and a click select. Each card: the radio mark, the time in the event zone and, on its second line, the event zone
// and the attendee's local time (row 100, call 4); the free dots (green = free, grey = OOF, DC-159) and the attendees'
// initials; a right slot. A conflict rides on the slot it breaks (row 100, DC-139): a flag Pill *Conflict* and its
// reason, and the slot can still be chosen. Blocked (DC-24, row 100 "D blocked + sentence"): a slot or the whole group
// keeps its tab stop, is aria-disabled, is described by his sentence (shown under it) and cannot be chosen.
// loading: skeleton cards at the final height. mode="view": the slots as values, no radios.

import { useCallback, useState, type KeyboardEvent, type ReactNode } from "react";
import { Avatars, Pill, Skeleton, type Blocked } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useRoving } from "../lib/useRoving";
import { useSvgId } from "../lib/useSvgId";
import { SCHEDULING_LABELS } from "./labels";

/** One attendee on a slot: their initials, and a dot that is green when the slot is free for them. */
export interface SlotAttendee {
  name: string;
  /** A candidate draws the rounded tile (DC-159: the candidate is a further dot). */
  candidate?: boolean | undefined;
  /** false: OOF or busy at that time; the dot greys. Default true. */
  free?: boolean | undefined;
}

/** row 100: the props a slot item carries beyond #27's time and attendees. */
export interface Slot {
  /** The slot's value. */
  id: string;
  /** The time in the event zone (a formatted date and time). */
  when: ReactNode;
  /** row 100's second line: the event zone, then an attendee's local time (shown in --text-3). */
  zone?: ReactNode | undefined;
  local?: ReactNode | undefined;
  attendees?: readonly SlotAttendee[] | undefined;
  /** A conflict this slot breaks: the reason words (*OOF · Marcus Bell*, *working hours*, *room*, *time zone*). */
  conflict?: { reason: ReactNode } | undefined;
  /** His sentence when this slot cannot be chosen. */
  blocked?: Blocked | undefined;
  /** Meta on the card's right. */
  right?: ReactNode | undefined;
  hs?: Hs | undefined;
}

export interface SlotsProps {
  items: readonly Slot[];
  /** The chosen slot's id (controlled) … */
  value?: string | undefined;
  /** … or the one chosen first (uncontrolled); without either, nothing is chosen until a slot is picked. */
  defaultValue?: string | undefined;
  onChange?: ((id: string) => void) | undefined;
  /** The group's name (default *proposed slots*). */
  label?: string | undefined;
  /** The whole list is blocked (an invalid sequence): his sentence. */
  blocked?: Blocked | undefined;
  loading?: boolean | number | undefined;
  mode?: "edit" | "view" | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

function SlotBody({ slot, ids }: { slot: Slot; ids: { when: string; line: string; conflict: string } }) {
  const attendees = slot.attendees ?? [];
  return (
    <>
      <span className="mc-slot-rd" aria-hidden="true" />
      <span className="mc-slot-w">
        <b id={ids.when}>{slot.when}</b>
        {slot.zone !== undefined || slot.local !== undefined ? (
          <span id={ids.line} className="mc-slot-z">
            {slot.zone}
            {slot.zone !== undefined && slot.local !== undefined ? " · " : null}
            {slot.local !== undefined ? <span className="mc-t3">{slot.local}</span> : null}
          </span>
        ) : null}
      </span>
      {slot.conflict ? (
        <span id={ids.conflict} className="mc-slot-c">
          <span className="mc-slot-cp"><Pill tone="flag">{SCHEDULING_LABELS.conflict}</Pill></span>
          <span className="mc-slot-cr">{slot.conflict.reason}</span>
        </span>
      ) : null}
      {attendees.length ? (
        <>
          <span className="mc-slot-dots" aria-hidden="true">
            {attendees.map((who, index) => <i key={index} className={who.free === false ? "mc-slot-off" : undefined} />)}
          </span>
          <Avatars items={attendees.map((who) => ({ name: who.name, candidate: who.candidate }))} />
        </>
      ) : null}
      {slot.right !== undefined ? <span className="mc-slot-r">{slot.right}</span> : null}
    </>
  );
}

function SlotRadio({
  slot, checked, groupBlocked, item, onChoose,
}: {
  slot: Slot; checked: boolean; groupBlocked: Blocked | undefined;
  item: ReturnType<ReturnType<typeof useRoving>["item"]>; onChoose: () => void;
}) {
  const base = useSvgId();
  const hot = useHs(slot.hs);
  const ids = { when: `${base}-w`, line: `${base}-z`, conflict: `${base}-c`, why: `${base}-why` };
  const blocked = slot.blocked ?? groupBlocked;
  const describedBy = [
    slot.zone !== undefined || slot.local !== undefined ? ids.line : null,
    slot.conflict ? ids.conflict : null,
    slot.blocked ? ids.why : null,
  ].filter(Boolean).join(" ") || undefined;
  const choose = () => { if (!blocked) onChoose(); };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      choose();
    }
  };
  return (
    <div className="mc-slot-cell">
      <div
        {...item}
        {...hot}
        role="radio"
        aria-checked={checked}
        aria-labelledby={ids.when}
        aria-describedby={describedBy}
        aria-disabled={blocked ? true : undefined}
        className={cx("mc-slot", checked && "mc-on", slot.conflict && "mc-slot-conflict", blocked && "mc-dis")}
        onClick={choose}
        onKeyDown={onKeyDown}
      >
        <SlotBody slot={slot} ids={ids} />
      </div>
      {slot.blocked ? <p id={ids.why} className="mc-slot-why">{slot.blocked.reason}</p> : null}
    </div>
  );
}

export function Slots({
  items, value, defaultValue, onChange, label = SCHEDULING_LABELS.proposedSlots, blocked, loading, mode, can, hs, className,
}: SlotsProps) {
  const base = useSvgId();
  const hot = useHs(hs);
  const [own, setOwn] = useState(defaultValue ?? "");
  const chosen = value ?? own;
  const choose = useCallback((id: string) => {
    if (value === undefined) setOwn(id);
    onChange?.(id);
  }, [value, onChange]);
  const active = items.findIndex((slot) => slot.id === chosen);
  const roving = useRoving({
    count: items.length,
    active: active >= 0 ? active : undefined,
    orientation: "vertical",
    selectOnFocus: true,
    onSelect: (index) => {
      const slot = items[index];
      if (slot && !slot.blocked && !blocked) choose(slot.id);
    },
  });
  if (can === false) return null;
  if (loading) {
    const count = typeof loading === "number" ? loading : 3;
    return (
      <div className={cx("mc-slots", className)} aria-busy="true">
        {Array.from({ length: count }, (_, index) => <Skeleton key={index} shape="input" height={58} />)}
      </div>
    );
  }
  if (mode === "view") {
    return (
      <div className={cx("mc-slots", "mc-slots-view", className)}>
        {items.map((slot) => (
          <div key={slot.id} className={cx("mc-slot", slot.id === chosen && "mc-on")}>
            <SlotBody slot={slot} ids={{ when: `${base}-${slot.id}-w`, line: `${base}-${slot.id}-z`, conflict: `${base}-${slot.id}-c` }} />
          </div>
        ))}
      </div>
    );
  }
  const whyId = `${base}-why`;
  return (
    <div className={cx("mc-slots-wrap", className)}>
      <div
        {...roving.container}
        {...hot}
        role="radiogroup"
        aria-label={label}
        aria-disabled={blocked ? true : undefined}
        aria-describedby={blocked ? whyId : undefined}
        className={cx("mc-slots", blocked && "mc-slots-dis")}
      >
        {items.map((slot, index) => (
          <SlotRadio
            key={slot.id}
            slot={slot}
            checked={slot.id === chosen}
            groupBlocked={blocked}
            item={roving.item(index)}
            onChoose={() => choose(slot.id)}
          />
        ))}
      </div>
      {blocked ? <p id={whyId} className="mc-slot-why">{blocked.reason}</p> : null}
    </div>
  );
}
